/**
 * Client-side delayed "Complete": the request is held for a few seconds so the user can undo.
 * Undo cancels locally and the server never sees it. If the page is hidden or closing while a
 * completion is pending, it's sent right away with fetch keepalive.
 */
export const UNDO_SECONDS = 8;

export const createPendingCompletions = ({
    send,
    sendKeepalive,
    delayMs = UNDO_SECONDS * 1000,
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = (id) => clearTimeout(id),
}) => {
    const pending = new Map(); // taskId -> { timer, body, resolve, reject }

    const fire = (taskId) => {
        const entry = pending.get(taskId);
        if (!entry) return;
        pending.delete(taskId);
        Promise.resolve()
            .then(() => send(taskId, entry.body))
            .then(entry.resolve, entry.reject);
    };

    return {
        /** Schedules a completion; resolves with the server response, or { undone: true }. */
        schedule(taskId, body) {
            this.undo(taskId);
            return new Promise((resolve, reject) => {
                const timer = setTimer(() => fire(taskId), delayMs);
                pending.set(taskId, { timer, body, resolve, reject });
            });
        },
        /** Several completions under one Undo: resolves with { taskId, result } or { taskId, error } for each. */
        scheduleMany(items) {
            return Promise.all(
                items.map(({ taskId, body }) =>
                    this.schedule(taskId, body).then(
                        (result) => ({ taskId, result }),
                        (error) => ({ taskId, error }),
                    ),
                ),
            );
        },
        undoMany(taskIds) {
            return taskIds.filter((id) => this.undo(id)).length > 0;
        },
        undo(taskId) {
            const entry = pending.get(taskId);
            if (!entry) return false;
            clearTimer(entry.timer);
            pending.delete(taskId);
            entry.resolve({ undone: true });
            return true;
        },
        isPending(taskId) {
            return pending.has(taskId);
        },
        /** Page is closing or hidden: send everything now so nothing is lost. */
        flushKeepalive() {
            Array.from(pending.entries()).forEach(([taskId, entry]) => {
                clearTimer(entry.timer);
                pending.delete(taskId);
                try {
                    sendKeepalive(taskId, entry.body);
                    entry.resolve({ flushed: true });
                } catch (e) {
                    entry.reject(e);
                }
            });
        },
        size() {
            return pending.size;
        },
    };
};
